import {
  ContentBlock,
  ContentState,
  convertFromRaw,
  RawDraftContentState,
} from "draft-js";

export const TEMPLATE_PART_BLOCK_KEY = "TEMPLATE";

/**
 * プリミティブ型
 */
type JsonPrimitive = string | number | boolean | null | undefined;

function isPrimitive(p: unknown): p is JsonPrimitive {
  if (p === null) {
    return true;
  }
  const type = typeof p;
  return (
    type === "string" ||
    type === "number" ||
    type === "boolean" ||
    type === "undefined"
  );
}

/**
 * JSONで表現可能なオブジェクト型
 */
type JsonObject = {
  [key: string]: unknown;
};

function isJsonObject(o: unknown): o is JsonObject {
  if (!o) {
    return false;
  }
  return typeof o === "object";
}

/**
 * @param json JSON要素
 * @param shouldConvert 変換する必要があるか
 * @param convert 変換関数
 * @param acc
 */
export function convertRecursion<T, U>(
  json: unknown,
  shouldConvert: (t: unknown) => t is T,
  convert: (t: T) => U,
  acc: JsonObject = {},
): unknown {
  if (shouldConvert(json)) {
    return convert(json);
  }

  if (isPrimitive(json)) {
    return json;
  }

  if (Array.isArray(json)) {
    return json.map((t) => convertRecursion(t, shouldConvert, convert));
  }

  if (isJsonObject(json)) {
    const [headKey, ...tailKeys] = Object.keys(json);
    if (!headKey) {
      return acc;
    }

    const tail = tailKeys.reduce((prev, current) => {
      return { ...prev, [current]: json[current] };
    }, {} as { [key: string]: unknown });

    const head = json[headKey];

    return convertRecursion(tail, shouldConvert, convert, {
      ...acc,
      [headKey]: convertRecursion(head, shouldConvert, convert, {}),
    });
  }
  throw new Error("保存されているページを正しく復元できませんでした");
}

/**
 * RawDraftContentStateかを判定
 * @param target
 */
export function isRawDraftContentState(
  target: unknown,
): target is RawDraftContentState {
  if (target == null) {
    return false;
  }

  if (!(typeof target === "object")) {
    return false;
  }

  if (!("blocks" in target)) {
    return false;
  }

  return "entityMap" in target;
}

/**
 * JSONからContentStateを復元してContentStateを返却する
 */
export function convertFromRawRecursively(
  draftContentState: object,
): ContentState {
  // まず全体をContentStateに復元
  const contentState = convertFromRaw(
    draftContentState as RawDraftContentState,
  );

  /* ContentStateをネストしているテンプレパーツが存在するため, 1回の変換ではContentStateが
      正しい形にならない場合がある.新しくContentBlockの配列を作成することで対応 */
  const blocks = contentState.getBlocksAsArray();
  const newBlocks = blocks.map((block) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const data = block.getData().get(TEMPLATE_PART_BLOCK_KEY);
    if (!data) {
      return block;
    }
    // 再帰的にContentBlockの中身を調べて, ContentStateをネストしているパーツだったら変換処理を行う
    const convertedData = convertRecursion(
      data,
      isRawDraftContentState,
      convertFromRaw,
    );

    // 新しくContentBlockを作成
    // ContentBlockにsetterやプロパティの一部を置き換えた新たなコピーの作成するメソッドは用意されていないためこの方法で作成
    const newBlock: ContentBlock = new ContentBlock({
      key: block.getKey(),
      type: block.getType(),
      text: block.getText(),
      characterList: block.getCharacterList(),
      depth: block.getDepth(),
      data: block.getData().set(TEMPLATE_PART_BLOCK_KEY, convertedData),
    });
    return newBlock;
  });

  return ContentState.createFromBlockArray(newBlocks);
}

// 画像設定されていなかった場合に表示するデフォルト画像
export const defaultImgSrc = "/customer/img/edit/page-create/image/image.jpg";
export const defaultSliderImgSrc =
  "/customer/img/edit/template-blocks/slider-dummy.jpg";
export const defaultSliderSimpleImgSrc =
  "/customer/img/edit/template-blocks/slider-simple-dummy.jpg";

export const defaultAvatarImgSrc =
  "/customer/img/edit/template-blocks/icon_man.svg";
export const defaultLogoImgSrc =
  "/customer/img/edit/template-blocks/logo-default.svg";
export const defaultProfileImgSrc =
  "/customer/img/edit/template-blocks/profile-default.png";
export const defaultImgSquareSrc =
  "/customer/img/edit/template-blocks/image-square-default.png";
export const defaultImgRectangleSrc =
  "/customer/img/edit/template-blocks/image-rectangle-default.png";

export const defaultImgVideoSrc =
  "/customer/img/edit/template-blocks/video-preview-default.png";
export const defaultVideoPlayButtonSrc =
  "/customer/img/edit/template-blocks/video-play-button.svg";

// Landing page component
export const defaultBgImgSrc =
  "/customer/img/edit/template-blocks/bg-default.png";

export const defaultServiceCardBgImgSrc =
  "/customer/img/edit/template-blocks/bg_event_default.png";
